import { Field, FormikHelpers, FormikProps } from "formik";
import { filter, find, get, sum, values } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import ExternalLink from "../../../../components/ExternalLink";
import FormProvider from "../../../../components/forms/FormProvider";
import HandleFieldChange from "../../../../components/forms/HandleFieldChange";
import Input from "../../../../components/forms/Input";
import { useHotspots } from "../../../../context/hotspots";
import { useWallets } from "../../../../context/wallets";
import { BONES_PER_HNT } from "../../../../utils/useHntMarket";

interface Props {}

const SetDonationStep: React.FC<Props> = () => {
  const location = useLocation();
  const history = useHistory();
  const { wallets, verifyWallet } = useWallets();
  const { workspaceHotspots, hotspotRewardsSummary } = useHotspots();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const wizardPageMatch = matchPath<{
    walletAddress: string;
    stepKey: string;
  }>(location.pathname, {
    path: "/workspace/wallets/verify/:walletAddress/:stepKey"
  });

  const wallet = useMemo(
    () =>
      find(values(wallets), {
        address: wizardPageMatch?.params.walletAddress
      }),
    [wallets, wizardPageMatch?.params.walletAddress]
  );

  useEffect(() => {
    if (!wallet) {
      history.push(
        `/workspace/wallets/verify/${wizardPageMatch?.params.walletAddress}/confirm-wallet`
      );
    }
  }, [history, wallet, wizardPageMatch?.params.walletAddress]);

  const averageHourlyWalletRewards = useMemo(() => {
    const weeklyWalletRewards =
      sum(
        filter(workspaceHotspots, {
          owner: wizardPageMatch?.params.walletAddress
        }).map(
          (hotspot) => get(hotspotRewardsSummary, [hotspot.id, "week"]) || 0
        )
      ) / BONES_PER_HNT;

    return Math.max(
      0.1,
      Math.round((weeklyWalletRewards / (7 * 24)) * 10) / 10
    );
  }, [
    hotspotRewardsSummary,
    wizardPageMatch?.params.walletAddress,
    workspaceHotspots
  ]);

  const initialValues = useMemo(
    () => ({
      miningHours: 1,
      amount: averageHourlyWalletRewards
    }),
    [averageHourlyWalletRewards]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      if (!wizardPageMatch?.params.walletAddress || !wallet) {
        return;
      }

      const apiResponse = await verifyWallet(
        wallet.uuid,
        Math.round(formValues.amount * 1000) / 1000
      );

      // The top level VerifyWalletWizardPage component will see the change in the wallet verification objects
      // and will take care of routing the user to the next page

      if (!apiResponse.status) {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [verifyWallet, wallet, wizardPageMatch?.params.walletAddress]
  );

  return (
    <>
      <p className="mb-4">
        You can anonymously verify this wallet by sending a donation from it,
        using the{" "}
        <ExternalLink
          className="inline-flex"
          text="Helium Wallet app"
          url="https://docs.helium.com/wallets/app-wallet"
        />
        .
      </p>
      <p className="mb-4">
        Some HNT must be sent to create a transaction that can be verified, to
        prove you own the wallet.
      </p>
      <p className="mb-4">
        Larger donations are always welcome, as the Hotspotty team has spent
        thousands of hours building this platform. However, our main motivation
        is to help build a more efficient People’s Network - so we have
        suggested a donation equivalent to just one hour of HNT mining.
      </p>
      <p className="mb-4">
        By verifying this wallet you’ll be able to carry out the trusted
        communication and collaboration with other hotspot owners, that is so
        essential for the Helium project to grow - and thrive.
      </p>

      <FormProvider
        classnames={{ form: "dark:bg-gray-800" }}
        error={errorMessage}
        formikProps={{
          onSubmit,
          initialValues,
          validationSchema: Yup.object().shape({
            miningHours: Yup.number()
              .min(0.1, "The minimum is 0.1 mining hours")
              .required("Required"),
            amount: Yup.number()
              .min(0.1, "The minimum amount is 0.1HNT")
              .required("Required")
          })
        }}
        orderedFieldNames={["amount"]}
        submitButtonText="Continue"
        submittingSubmitButtonText="Processing"
      >
        {({
          isSubmitting,
          errors,
          setFieldValue,
          values
        }: FormikProps<FormValues>) => {
          return (
            <>
              <div className="flex items-start">
                <Field
                  id="miningHours"
                  name="miningHours"
                  label="Mining hours to donate"
                  type="number"
                  component={Input}
                  classnames={{
                    wrapper: "relative mb-4 w-40 mr-4"
                  }}
                  disabled={isSubmitting}
                />

                <Field
                  id="amount"
                  name="amount"
                  label="Donation amount"
                  type="number"
                  step={0.1}
                  component={Input}
                  classnames={{
                    wrapper: "relative mb-4 w-32",
                    input: "pr-12"
                  }}
                  disabled={isSubmitting}
                >
                  {!errors.amount && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 dark:text-white">HNT</span>
                    </div>
                  )}
                </Field>
              </div>

              <HandleFieldChange<FormValues>
                name="miningHours"
                onChange={(newMiningHours: number) => {
                  if (
                    values.miningHours !==
                    Math.round(
                      (values.amount / averageHourlyWalletRewards) * 100
                    ) /
                      100
                  ) {
                    setFieldValue(
                      "amount",
                      Math.round(
                        newMiningHours * averageHourlyWalletRewards * 10
                      ) / 10
                    );
                  }
                }}
              />

              <HandleFieldChange<FormValues>
                name="amount"
                onChange={(newAmount: number) => {
                  if (
                    values.amount !==
                    Math.round(
                      values.miningHours * averageHourlyWalletRewards * 10
                    ) /
                      10
                  ) {
                    setFieldValue(
                      "miningHours",
                      Math.round(
                        (newAmount / averageHourlyWalletRewards) * 100
                      ) / 100
                    );
                  }
                }}
              />
            </>
          );
        }}
      </FormProvider>
    </>
  );
};

export default SetDonationStep;

//
// Utils
//

interface FormValues {
  miningHours: number;
  amount: number;
}
